import React, { useState, useEffect } from 'react';
import {
  Title,
  Grid,
  Paper,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Loader,
  Center
} from '@mantine/core';
import {
  IconUsers,
  IconSchool,
  IconBook,
  IconCurrencyRupee,
  IconTrendingUp
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { memberService } from '../../services/memberService';
import { batchService } from '../../services/batchService';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [memberStats, batchData] = await Promise.all([
        memberService.getStats(),
        batchService.getBatches()
      ]);
      setStats(memberStats);
      setBatches(batchData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  const dashboardStats = [
    {
      title: 'Total Members',
      value: stats.total_members || 0,
      icon: IconUsers,
      color: 'blue',
      description: 'Active members enrolled'
    },
    {
      title: 'Active Batches',
      value: batches.filter(b => !b.bfc_admin_stop && !b.bfc_cap_time_reached).length,
      icon: IconSchool,
      color: 'green',
      description: 'Currently accepting members'
    },
    {
      title: 'Razorpay Payments',
      value: stats.razorpay_members || 0,
      icon: IconCurrencyRupee,
      color: 'yellow',
      description: 'Automated payments received'
    },
    {
      title: 'Recent Members',
      value: stats.recent_members || 0,
      icon: IconTrendingUp,
      color: 'purple',
      description: 'Joined in last 7 days'
    }
  ];

  const quickActions = [
    {
      title: 'Manage Batches',
      description: 'Create and manage course batches',
      action: () => navigate('/admin/batches'),
      color: 'blue'
    },
    {
      title: 'Add Member',
      description: 'Manually add new members',
      action: () => navigate('/admin/members'),
      color: 'green'
    },
    {
      title: 'Curriculum Setup',
      description: 'Manage course curriculums',
      action: () => navigate('/admin/curriculum'),
      color: 'purple'
    },
    {
      title: 'Settings',
      description: 'Configure batch settings',
      action: () => navigate('/admin/settings'),
      color: 'orange'
    }
  ];

  return (
    <Stack>
      <Title order={1}>Dashboard</Title>

      <Grid>
        {dashboardStats.map((stat) => (
          <Grid.Col key={stat.title} span={{ base: 12, sm: 6, md: 3 }}>
            <Paper withBorder p="md" radius="md">
              <Group justify="space-between">
                <Stack gap="xs">
                  <Text size="xs" c="dimmed" fw={700} style={{ textTransform: 'uppercase' }}>
                    {stat.title}
                  </Text>
                  <Text fw={700} size="xl">
                    {stat.value}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {stat.description}
                  </Text>
                </Stack>
                <stat.icon size={32} color={`var(--mantine-color-${stat.color}-6)`} />
              </Group>
            </Paper>
          </Grid.Col>
        ))}
      </Grid>

      <Title order={2} mt="xl">Quick Actions</Title>

      <Grid>
        {quickActions.map((action) => (
          <Grid.Col key={action.title} span={{ base: 12, sm: 6 }}>
            <Paper withBorder p="lg" radius="md" style={{ cursor: 'pointer' }} onClick={action.action}>
              <Stack>
                <Group justify="space-between">
                  <Text fw={600} size="lg">
                    {action.title}
                  </Text>
                  <Badge color={action.color} variant="light">
                    Action
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">
                  {action.description}
                </Text>
                <Button variant="light" color={action.color} size="sm" mt="sm">
                  Go to {action.title}
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>
        ))}
      </Grid>

      <Paper withBorder p="lg" radius="md" mt="xl">
        <Stack>
          <Group justify="space-between">
            <Title order={3}>Recent Activity</Title>
            <Badge color="gray" variant="light">
              Coming Soon
            </Badge>
          </Group>
          <Text c="dimmed">
            Recent member registrations, batch activities, and system events will appear here.
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default AdminDashboard;